import { Capacitor } from "@capacitor/core";
import { useIonToast } from "@ionic/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { CognitoUserPool } from "amazon-cognito-identity-js";
import App from "App";
import { UserModel, UserResponse } from "model";
import singletonStorage from "stores/store";
import {
  useAuth as _useAuth,
  AuthProvider,
  DesignSystemProvider,
  FeatureFlagProvider,
} from "swing-components";

import {
  api,
  APP_AUTH_TOKEN_COOKIE_NAME,
  COGNITO_APP_CLIENT_ID,
  COGNITO_USER_POOL_ID,
  FIREBASE_AUTH_CONFIG_API_KEY,
  FIREBASE_AUTH_CONFIG_APP_ID,
  FIREBASE_AUTH_CONFIG_AUTH_DOMAIN,
  FIREBASE_AUTH_CONFIG_MESSAGING_SENDER_ID,
  FIREBASE_AUTH_CONFIG_PROJECT_ID,
  FIREBASE_AUTH_CONFIG_STORAGE_BUCKET,
  LEGACY_AUTH_TOKEN_COOKIE_NAME,
  LEGACY_COOKIE_DOMAIN,
  postUserMe,
  vgsApi,
} from "~utils";

/********* Constants ********/
const QUERY_CLIENT = new QueryClient();

const STORAGE_AUTH_KEY = "auth";

/********* User APIs *********/
const userApi = async () => (await api<UserResponse>({ url: "/api/admin/me" })).data;
const userSyncApi = async () => await postUserMe();
// COGNITO USER POOL INITIALIZATION
const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_USER_POOL_ID,
  ClientId: COGNITO_APP_CLIENT_ID,
  // defaults to localStorage but we want our own storage implementation
  Storage: singletonStorage,
});

/********* Component *********/
export function AuthWrapper() {
  const [present] = useIonToast();
  QUERY_CLIENT.defaultMutationOptions({
    ...QUERY_CLIENT.defaultMutationOptions,
    onError: (error) => {
      present({
        position: "top",
        message: `Something went wrong: ${error}`,
        duration: 2000,
      });
    },
  });

  return (
    <QueryClientProvider client={QUERY_CLIENT}>
      <AuthProvider
        api={api}
        appAuthCookieName={APP_AUTH_TOKEN_COOKIE_NAME}
        authCookieDomain={LEGACY_COOKIE_DOMAIN}
        firebaseConfig={{
          apiKey: FIREBASE_AUTH_CONFIG_API_KEY,
          appId: FIREBASE_AUTH_CONFIG_APP_ID,
          authDomain: FIREBASE_AUTH_CONFIG_AUTH_DOMAIN,
          messagingSenderId: FIREBASE_AUTH_CONFIG_MESSAGING_SENDER_ID,
          projectId: FIREBASE_AUTH_CONFIG_PROJECT_ID,
          storageBucket: FIREBASE_AUTH_CONFIG_STORAGE_BUCKET,
        }}
        isWeb={Capacitor.getPlatform() === "web"}
        legacyAuthCookieName={LEGACY_AUTH_TOKEN_COOKIE_NAME}
        queryClient={QUERY_CLIENT}
        storage={singletonStorage}
        storageAuthKey={STORAGE_AUTH_KEY}
        userPool={userPool}
        userApi={userApi}
        userSyncApi={userSyncApi}
        vgsApi={vgsApi}
      >
        <DesignSystemProvider>
          <FeatureFlagProvider api={api}>
            <App />
          </FeatureFlagProvider>
        </DesignSystemProvider>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

/********* Helper Components *********/
type User = Awaited<ReturnType<typeof userApi>>;

/**
 * Typed `useAuth` for the Swing School user. This should be used vs the
 * swing-components one.
 */
export function useAuth() {
  // note: this relies on typed endpoints which isn't in place yet,
  // therefore we will override `userInfo` that is returned to be
  // `UserModel` as that is what the admin app is currently expecting.
  // once types are in place we can just use `return _useAuth<User>();`
  const auth = _useAuth<User>();
  return {
    ...auth,
    userInfo: new UserModel(auth.userInfo as User),
  };
}
